import { styled } from '@linaria/react';
import { newCOLORS } from '../../../../styles/colors';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import type { ToolApproval, ToolApprovalDecision, ToolApprovalResolution } from '../../flora.type';
import { AgentPromptRejection } from './agent-prompt-rejection';

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  jira_create_ticket: 'Create Jira Ticket',
  jira_add_comment: 'Add Jira Comment',
  jira_link_issues: 'Update Jira Ticket Links',
  jira_transition_ticket: 'Change Jira Ticket Status',
  ado_create_work_item: 'Create Azure DevOps Work Item',
};

interface AgentToolApprovalProps {
  toolApproval: ToolApproval;
  decision?: ToolApprovalResolution;
  onApprove?: (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => void;
  /** Why the backend refused the last decision; shown while the buttons are live again. */
  rejection?: string;
}

export function AgentToolApproval({ toolApproval, decision, onApprove, rejection }: AgentToolApprovalProps) {
  const actionRequests = toolApproval.actionRequests;
  const isResolved = Boolean(decision);
  const isApproved = decision === 'approved' || decision === 'approved_always';

  return (
    <Container $resolved={isResolved} $rejected={decision === 'rejected'}>
      {actionRequests.map((actionRequest, index) => {
        const displayName = TOOL_DISPLAY_NAMES[actionRequest.name] || actionRequest.name;
        return (
          <ActionSection key={`${actionRequest.name}-${index}`}>
            <Header $rejected={decision === 'rejected'}>
              {/* Icon names must exist in Material Icons (not Symbols) — unknown
                  names render as raw text and blow up this header's layout. */}
              {decision === 'rejected' ? (
                <Icon name="close" size={20} color={newCOLORS.red} />
              ) : isApproved ? (
                <Icon name="check" size={20} color={newCOLORS.green} />
              ) : (
                <Icon name="gpp_maybe" size={20} color={newCOLORS.blue} />
              )}
              <Text size="small" weight="bold" color="var(--ink-dark)">
                {decision === 'rejected'
                  ? `Rejected: ${displayName}`
                  : isApproved
                    ? `Approved: ${displayName}`
                    : `Approval Required: ${displayName}`}
              </Text>
            </Header>
            <Body>
              <DetailsList>
                {Object.entries(actionRequest.args ?? {}).map(([key, value]) => (
                  <DetailRow key={key}>
                    <DetailLabel size="tiny" weight="bold" color="var(--ink-lighter)">
                      {key}
                    </DetailLabel>
                    <DetailValue>{typeof value === 'string' ? value : JSON.stringify(value, null, 2)}</DetailValue>
                  </DetailRow>
                ))}
              </DetailsList>
            </Body>
          </ActionSection>
        );
      })}
      {decision === 'approved_always' && (
        <AlwaysApprovedNote>
          <Text size="tiny" color="var(--ink-lighter)">
            Flora won't ask again for these actions — manage this in Customize Flora.
          </Text>
        </AlwaysApprovedNote>
      )}
      {!isResolved && onApprove && rejection && <AgentPromptRejection message={rejection} />}
      {!isResolved && onApprove && (
        <ButtonRow>
          <Button
            size="xs"
            variant="secondary"
            leftSection={<Icon name="check" size={16} color="currentColor" style={{ cursor: 'pointer' }} />}
            onClick={() => onApprove('approve', toolApproval)}
          >
            Approve
          </Button>
          <Button
            size="xs"
            variant="secondary"
            leftSection={<Icon name="done_all" size={16} color="currentColor" style={{ cursor: 'pointer' }} />}
            onClick={() => onApprove('approve_always', toolApproval)}
          >
            Always Approve
          </Button>
          <Button
            size="xs"
            variant="warning"
            leftSection={<Icon name="close" size={16} color="currentColor" style={{ cursor: 'pointer' }} />}
            onClick={() => onApprove('reject', toolApproval)}
          >
            Reject
          </Button>
        </ButtonRow>
      )}
    </Container>
  );
}

const Container = styled.div<{ $resolved?: boolean; $rejected?: boolean }>`
  margin: 8px 0;
  border: 1px solid ${(props) => (props.$rejected ? `${newCOLORS.red}33` : `${newCOLORS.blue}33`)};
  border-radius: 10px;
  background: var(--sky-lightest);
  overflow: hidden;
  opacity: ${(props) => (props.$resolved ? 0.75 : 1)};
`;

const ActionSection = styled.div`
  &:not(:last-of-type) {
    border-bottom: 1px solid var(--sky-light);
  }
`;

const Header = styled.div<{ $rejected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--sky-light);
  background: ${(props) => (props.$rejected ? `${newCOLORS.red}08` : 'var(--base-white)')};
`;

const Body = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLabel = styled(Text)`
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.pre`
  margin: 0;
  padding: 6px 10px;
  background: var(--sky-lighter);
  border-radius: 6px;
  font-size: 12.5px;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 150px;
  overflow-y: auto;
  line-height: 1.5;
  color: var(--ink-dark);
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  padding: 12px;
`;

const AlwaysApprovedNote = styled.div`
  padding: 0 12px 10px;
`;
